/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import {
	Tab,
	Tabs,
	Typography,
	TextField,
	Button,
	IconButton,
	Divider,
	Alert,
	Tooltip,
	Box,
	Card,
	CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import "./Profile.css";
import PageTitle from "../../components/PageTitle/PageTitle";
import Datagrid from "../../components/Datagrid/Datagrid";
import Settings from "../Settings/Settings";
import EditUserForm from "../../components/EditUserForm/EditUserForm";
import ProfileForm from "../../components/ProfileForm/ProfileForm";
import PasswordForm from "../../components/PasswordForm/PasswordForm";
import NotificationPreferences from "../../components/NotificationPreferences/NotificationPreferences";
import NewUserForm from "../../components/NewUserForm/NewUserForm";
import Swal from "sweetalert2";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import ManageDocuments from "../ManageDocuments/ManageDocuments";
import { useSite } from "../../context/siteContext";
import ManageSites from "../ManageSites/ManageSites";
import { useAuth } from "../../context/authContext";
import { useTranslation } from "react-i18next";
import { BUTTON, LABELS, UI_TEXT } from "../../constants";
import { getEndpointData } from "../../services/adminService";
import { getCustomerByIdForCusSites } from "../../services/scheduleOrderService";

function Profile() {
	// Retrieve and parse user details from session storage

	// State for tab navigation
	const [tab, setTab] = useState(0);
	const { t } = useTranslation();

	const [currentUser, setCurrentUser] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [allUsers, setAllUsers] = useState([]);
	const [allCustomers, setAllCustomers] = useState([]);
	const [allSites, setAllSites] = useState([]);
	const [assignedSitesLoc, setAssignedSitesLoc] = useState([]);
	const [selectedUser, setSelectedUser] = useState(null); // State for selected user
	const [newUser, setNewUser] = useState({
		user_first_name: "",
		user_last_name: "",
		user_email: "",
		user_phone: "",
		user_role: "",
		user_status: "0", // Default status to "Pending"
	});
	const [createNewUser, setCreateNewUser] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [billingAddress, setBillingAddress] = useState("");
	const [shippingAddress, setShippingAddress] = useState("");
	const [selectedUsersRows, setSelectedUsersRows] = useState([]);
	const [allUsersKey, setAllUsersKey] = useState(Date.now());
	const { selectedSite, siteData, selectedSiteSName, selectedSiteData } = useSite();
    const { user, hasPermission } = useAuth();
    const canResetPassword = hasPermission('profile.allow_password_reset');
    const canNotifPrefs = hasPermission('profile.allow_notification_preferences');
	// Handle tab changes

	useEffect(() => {
		// Fetch the user from sessionStorage when the component mounts
		setCurrentUser(user);
	}, []);

	useEffect(() => {
		const getAddress = async () => {
			try {
				const site = siteData.filter((i) => i.customer_site_number === selectedSite)[0];
				setShippingAddress(site.address_full_ln);
				const response = await getCustomerByIdForCusSites(site.cus_id);
				if (response.status) {
					setBillingAddress(response.data.address_full_cus);
				}
			} catch (err) {
				console.error(err);
			}
		};

		if (selectedSite) getAddress();
	}, [selectedSite]);

	useEffect(() => {
		const fetchData = async (endpoint, setState) => {
			try {
				const response = await getEndpointData(endpoint);
				if (response.data.length > 0) {
					setState(response.data);
				} else {
					setError(`Failed to fetch data: No data found for ${endpoint}.`);
				}
			} catch (err) {
				setError(`${t('GENERAL.FAILED_TO_FETCH_DATA_FROM')} ${endpoint}: ${err.message}`);
			} finally {
				setLoading(false);
			}
		};

		if (currentUser && currentUser.user_role === "Admin") {
			setLoading(true);
			fetchData("/users", setAllUsers);
			fetchData("/cus_sites/getcustomers", setAllCustomers);
			fetchData("/cus_sites/getsites", setAllSites);
		} else {
			setLoading(false);
		}
	}, [currentUser]);

	useEffect(() => {
		refreshAllUsersKey();
	}, [allUsers]);

	const refreshAllUsersKey = () => setAllUsersKey(Date.now());

	const handleTabChange = (event, newValue) => {
		setTab(newValue);
	};

	const toggleEditing = () => {
		setIsEditing(!isEditing);
	};

	return (
		<>
			{!loading ? (
				<div className="profile-container">
					<PageTitle title={t('LABELS.PROFILE')} />
                    <Tabs
                        value={tab}
                        onChange={handleTabChange}
                        aria-label="profile page tabs"
                        className="profile-tabs"
						data-tour="profile-tab">
                    <Tab label={t('LABELS.MY_INFO')} />
                    {canResetPassword && <Tab label={t('LABELS.UPDATE_PASSWORDS')} />}
                    {canNotifPrefs && <Tab label={t('UI_TEXT.NOTIFICATIONS_1')} />}
                    </Tabs>

					<div className="tab-content">
						{tab === 0 && (
							<>
								<Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', columnGap: 4, px: 2, pt: 1, }}>
									<Box sx={{ display: 'flex', flexDirection: 'column', width: "70%" }}  data-tour="profile-main">
										<Box
											display="flex"
											flexDirection="row"
											justifyContent="space-between"
											alignItems="center"
											width="100%">
											<Typography variant="h6" gutterBottom mb={2}>
												{t('UI_TEXT.MY_INFORMATION')}
											</Typography>
											<Button
												variant={'text'}
												color={isEditing ? 'warning' : 'primary'}
												startIcon={<EditIcon />}
												onClick={toggleEditing}
												className="edit-button">
												{isEditing ? 'Cancel Editing' : t('BUTTON.EDIT_PROFILE')}
											</Button>
										</Box>
										<ProfileForm
											currentUser={currentUser}
											setCurrentUser={setCurrentUser}
											isEditing={isEditing}
											setIsEditing={setIsEditing}
										/>
									</Box>
									<Divider orientation="vertical" flexItem />
									<Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, width: '100%' }}>
										<Box
											display="flex"
											flexDirection="row"
											justifyContent="space-between"
											alignItems="center"
											width="100%">
											<Typography variant="h6" gutterBottom mb={2}>
												{t('LABELS.ADDRESS')}
											</Typography>

										</Box>
										<Typography variant="body2" color="text.secondary" gutterBottom mb={2}>
											Showing Address for <b>{selectedSiteData?.customer_site_name} ({selectedSite})</b>
								</Typography>
										<Box
											display="flex"
											flexDirection={"column"}
											flexWrap="wrap"
											justifyContent="space-between"
											alignItems="flex-start"
											gap="20px"
											width="100%"

											sx={{}}>
											{/* Shipping Address */}
											<Box
												flex="1"
												minWidth="300px"
												width={"100%"}
												padding="20px"
												sx={{
													backgroundColor: "#ffffff",
													borderRadius: "8px",
													boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
												}}>
												<Typography
													variant="h6"
													sx={{
														color: "var(--primary-main)",
														fontWeight: "bold",
														marginBottom: "15px",
													}}>
													{t('UI_TEXT.SHIPPING_ADDRESS')}
												</Typography>
												<Typography variant="body1" color="text.secondary" gutterBottom>
													{/* Replace with dynamic content */}
													{shippingAddress}
												</Typography>
											</Box>

											{/* Billing Address */}
											<Box
												flex="1"
												minWidth="300px"
												padding="20px"
												width={"100%"}
												sx={{
													backgroundColor: "#ffffff",
													borderRadius: "8px",
													boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
												}}>
												<Typography
													variant="h6"
													sx={{
														color: "var(--primary-main)",
														fontWeight: "bold",
														marginBottom: "15px",
													}}>
													{t('UI_TEXT.BILLING_ADDRESS')}
												</Typography>
												<Typography variant="body1" color="text.secondary" gutterBottom>
													{/* Replace with dynamic content */}
													{billingAddress}
												</Typography>
											</Box>
										</Box>

									</Box>

								</Box>

							</>
						)}
                    {(() => {
                        const pwIndex = canResetPassword ? 1 : -1;
                        const notifIndex = canResetPassword ? (canNotifPrefs ? 2 : -1) : (canNotifPrefs ? 1 : -1);
                        return (
                            <>
                                {pwIndex === tab && (
                                    <div className="update-password-section">
                                        <Typography variant="h6" gutterBottom mb={2}>
                                            {t('LABELS.UPDATE_PASSWORDS')}
                                        </Typography>
                                        <PasswordForm currentUser={currentUser} />
                                    </div>
                                )}
                                {notifIndex === tab && (
                                    <div className="notification-preferences-section">
                                        <NotificationPreferences />
                                    </div>
                                )}
                            </>
                        );
                    })()}



					</div>
				</div>
			) : (
				<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "40vh" }}>
					<CircularProgress />
				</Box>
			)}
		</>
	);
}

export default Profile;
